import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IFormHook } from '../models';
import { Form } from '@src/atoms/Form';
import type { IChangeableAndSubmittable, ILegoElem } from '../_Elem/models';
import { LegoFormContext } from './LegoFormContext';
import { isEqual } from 'lodash';
import { AnyType } from '@src/models/common';
import { IComponentChildrenProps } from '@src/models/props';
import { generateYupValidationSchema, reBuildElems } from '../utils';

interface IProps extends IChangeableAndSubmittable, IComponentChildrenProps {
  elems?: ILegoElem[];
  value?: AnyType;
}

export const LegoFormProvider: FC<IProps> = ({ elems, value, onChange, onSubmit, children }) => {
  const { formState, handleSubmit, resetField, setValue, getValues, watch, register, trigger } = useForm<AnyType>({
    resolver: async (data, context, options) => {
      // Если не делать колбечный запуск валидаторов, то в случае сложных форм,
      // которые частично рисуются через existsCb или replaceCb на элементе
      // todo: для оптимизации - врубать такой случай если есть existsCb и replaceCb
      const validationSchema = generateYupValidationSchema(reBuildElems(elems, data));
      return yupResolver(validationSchema)(data, context, options);
    },
    mode: 'onChange',
    defaultValues: value || {},
    values: value,
  });
  const { errors } = formState;

  const watchAllFields = onChange ? watch() : undefined;

  useEffect(() => {
    if (onChange) {
      // срабатыает после setValue на лего и
      // после передачи этого value в props-ы в лего
      // Сравнение isEqual нужено пока что так как watchAllFields,
      // которое мы тут получаем хоть и такое же на виж, но имеет
      // различнуб с value ссылку. Скорее всего это происходит
      // из-за того, что watchAllFields это форма из этого lego, а
      // value приходит из родительской формы
      if (!isEqual(value, watchAllFields)) {
        onChange(watchAllFields);
      }
    }
  }, [onChange, value, watchAllFields]);

  const emptyOnSubmitHandle = useCallback(() => {}, []);
  // Делаем так, чтобы просто пока что не убирать useForm
  // Далее в целях оптимизации при отсутствии submit-а не будет react hooks forms
  const handleSubmitFn = useMemo(() => onSubmit || emptyOnSubmitHandle, [onSubmit, emptyOnSubmitHandle]);

  const handleSetValueAndRevalidate = useCallback(
    (elemId: string, value: AnyType) => {
      setValue(elemId, value);
      // Если не вызывать trigger, то форма не будет валидироваться автоматом при изменении значения.
      // Например могут возникать ситуации, когда на незаполенном required поле после submit-а
      // показывается ошибка, что нужно поле заполнить. Далее мы значение выбираем,
      // но ошибка не уходит пока не нажмешь самбмит, что крайне странно выглядит.
      // При этом этот код вызывает показ ошибок при сбрасывании значений по depends условиям,
      // но выглядит уже не очень, но без него вся форма, хотть и очищена может все еще считаться валидной.
      // Получается непонятно пока как решаемый момент.
      trigger(elemId);
    },
    [setValue, trigger],
  );

  const formHook: IFormHook = {
    errors,
    register,
    resetField,
    trigger,
    setValue,
    getValues,
    watch,
    submitForm: handleSubmitFn,
    setValueAndRevalidate: handleSetValueAndRevalidate,
  };

  return (
    <LegoFormContext.Provider
      value={{
        formHook,
      }}
    >
      <Form onSubmit={handleSubmit(handleSubmitFn)}>{children}</Form>
    </LegoFormContext.Provider>
  );
};
